// scroll functions and listeners

// imports --------------------------------------------------------------------
import * as Vars from './0-vars.js';
import * as Once from './2-once.js';
import * as Resize from './4-resize.js';

// exported variables ---------------------------------------------------------
export let scrollTop = 0;
export let scrollLeft = 0;
export let windowBottom = window.innerHeight;
export let windowRight = window.innerWidth;

export let atBodyBottom = false;
export let atBodyRight = false;
export let isScrolling = false;

// local variables ------------------------------------------------------------
let scrollDuration = 35; // default = 35
const scrollOffset = 15; // how much from bottom / top to consider as "at bottom / top"

let interactionTimeout;


// scroll listeners ------------------------------------------------------------
export const initScroll = () => {
  // scroll listener
  window.addEventListener('scroll', setScroll, {passive: true});

  // interactions
  document.body.addEventListener('touchstart', resetTimeout, {passive: true});
  document.body.addEventListener('touchmove', resetTimeout, {passive: true});
  document.body.addEventListener('mousedown', resetTimeout, {passive: true});
  document.body.addEventListener('mousemove', resetTimeout, {passive: true});
};

// scroll function ------------------------------------------------------------
export const setScroll = () => {
  // do scrolling actions
  // console.log('setScroll');

  resetTimeout();

  if ('fixer' in Once && typeof Once.fixer?.bodyFixed !== 'undefined') {
    if (Once.fixer.bodyFixed !== true) {
      updateFromScroll();
    }
  } else {
    updateFromScroll();
  }
};

const updateFromScroll = () => {
  scrollTop = window.scrollY || document.documentElement.scrollTop;
  scrollLeft = window.scrollX || document.documentElement.scrollLeft;

  windowBottom = scrollTop + window.innerHeight;
  windowRight = scrollLeft + window.innerWidth;

  // use >= becaues of ios scrolling past the max, -1 for smooth scroll
  atBodyBottom = scrollTop >= Resize.maxBodyScrollTop - scrollOffset;
  atBodyRight = scrollLeft >= Resize.maxBodyScrollLeft - scrollOffset;

  // console.log('updateFromScroll', {windowBottom});

  // shrink content
  const pageContentEl = document.querySelector('.page--' + $pageType + ' .page__content');
  if (pageContentEl) {
    const rect = pageContentEl.getBoundingClientRect();
    const fromBottom = rect.bottom - Resize.windowInnerHeight;

    if (Vars.blTouch) {
      // console.log({fromBottom});
      if (fromBottom < 0 && $template !== 'iframe') {
        document.body.classList.add('body--shrink');
      } else {
        document.body.classList.remove('body--shrink');
      }
    } else {
      if ($template === 'iframe') {
        pageContentEl.style.setProperty('--scale', 1);
      } else {
        let fractionFromBottom = (fromBottom / Resize.fullTouch) * -1;
        if (fractionFromBottom < 0) fractionFromBottom = 0;

        // console.log({fractionFromBottom});
        const scale = 1 - fractionFromBottom * .2;
        pageContentEl.style.setProperty('--scale', scale);
      }
    }
  }

  // blur drawing
  if (scrollTop > 15) {
    document.body.classList.add('body--scrolled');
  } else {
    if($template === 'iframe') {
      document.body.classList.add('body--scrolled');
    } else {
      document.body.classList.remove('body--scrolled');
    }
  }

  if (atBodyBottom) {
    document.body.classList.add('body--at-bottom');
  } else {
    document.body.classList.remove('body--at-bottom');
  }

  // console.log('updateFromScroll', {scrollTop, scrollLeft, atBodyBottom, atBodyRight});

  // switch ($pageType) {
  //
  // }
};

// functions -------------------------------------------------------------------
const resetTimeout = () => {
  // console.log('resetTimeout');
  document.body.classList.remove('body--inactive');

  clearTimeout(interactionTimeout);
  interactionTimeout = setTimeout(() => {
    document.body.classList.add('body--inactive');
  }, 8000);
};

export const checkBlockScroll = () => {
  // console.log('checkBlockScroll');
  const hasFixer = 'fixer' in Once && typeof Once.fixer?.bodyFixed !== 'undefined';
  const hasAnimator = 'animator' in Once && typeof Once.animator?.disableSmoothScroll !== 'undefined';
  if (document.querySelector('.popover--active, .body--nav, .media--fullscreen')) {
    if (hasFixer) Once.fixer.fixBody();
    if (hasAnimator) Once.animator.disableSmoothScroll();
  } else {
    if (hasFixer) Once.fixer.unfixBody();
    if (hasAnimator && !isScrolling) Once.animator.enableSmoothScroll();
    if ('saveOffsets' in Resize) Resize.saveOffsets();
  }
};

export const scrollAboveFooter = (callback = '') => {
  const selector = '.page--' + $pageType + ' .page__sections';
  const pageSectionsEl = document.querySelector(selector);
  // console.log({$pageType, pageSectionsEl});
  if (pageSectionsEl) {
    const rect = pageSectionsEl.getBoundingClientRect();
    // console.log(rect.bottom, Resize.fullTouch);
    if (rect.bottom < Resize.fullTouch - 2) {
      // console.log('footer in view');
      // -1 so the page scale back to its original size
      animateScrollTop(Resize.documentHeight - Resize.fullTouch * 2 - 1, document.body, callback);
    } else {
      setTimeout(callback, Vars.transitionDefault);
    }
  }
};

export const scrollToEl = (el, returnFunction = '', offset = Resize.headerHeight + 20) => {
  const rect = el.getBoundingClientRect();
  const top = rect.top + scrollTop - offset;
  animateScrollTop(top, document.body, returnFunction);
};

export const animateScrollTop = (
  scroll,
  element = document.body,
  returnFunction = '',
  useMax = true,
) => {
  // console.log('animateScrollTop', {scroll}, {element}, {returnFunction}, {useMax});

  let height;
  let scrollHeight;
  if (element.tagName === 'BODY') {
    height = window.innerHeight;
    scrollHeight = document.documentElement.scrollHeight;

    if ('animator' in Once) Once.animator.disableSmoothScroll();
  } else {
    // not body
    height = element.offsetHeight;
    scrollHeight = element.scrollHeight;
  }

  if (useMax) {
    // maxscroll is where the scroll reaches the bottom
    // don't go past that or the animation will not be smooth
    const maxScroll = scrollHeight - height;
    if (scroll > maxScroll) scroll = maxScroll;
    if (scroll < 0) scroll = 0;
  }

  // needs to change for body because of browser differences
  if (element.tagName === 'BODY') element = document.documentElement;

  // console.log({height}, {scroll});

  scrollToPosition(scroll, 'top', element, returnFunction);
};

export const animateScrollLeft = (
  scroll,
  element = document.body,
  returnFunction = '',
  useMax = true,
) => {
  // console.log('animateScrollLeft', {scroll}, {element}, {returnFunction}, {useMax});

  let width;
  let scrollWidth;
  if (element.tagName === 'BODY') {
    width = window.innerWidth;
    scrollWidth = document.documentElement.scrollWidth;
  } else {
    // not body
    width = element.offsetWidth;
    scrollWidth = element.scrollWidth;
  }

  if (useMax) {
    // maxscroll is where the scroll reaches the side
    const maxScroll = scrollWidth - width;
    if (scroll > maxScroll) scroll = maxScroll;
    if (scroll < 0) scroll = 0;
  }

  // needs to change for body because of browser differences
  if (element.tagName === 'BODY') element = document.documentElement;

  // console.log({width}, {scroll});

  scrollToPosition(scroll, 'left', element, returnFunction);
};

const scrollToPosition = (to, direction = 'top', element, returnFunction = '') => {
  // get document scroll
  let start;
  if (element == document.documentElement) {
    start = direction === 'top' ? window.scrollY || element.scrollTop : window.scrollX || element.scrollLeft;
  } else {
    start = direction === 'top' ? element.scrollTop : element.scrollLeft;
  }

  const change = to - start;
  if (change < 1 && change > -1) {
    if (element == document.documentElement) checkBlockScroll();
    if (typeof returnFunction === 'function') returnFunction();
    return;
  }

  isScrolling = true;
  let duration = Math.sqrt(Math.abs(change)) * scrollDuration;
  if (duration < Vars.transitionDefault) duration = Vars.transitionDefault;
  const increment = 15;
  let currentTime = 0;
  // console.log(element.tagName, {start, change, duration, increment, currentTime});

  const easeInOutQuart = t => (t < 0.5) ? (8 * (t ** 4)) : (1 - (8 * ((--t) * (t ** 3))));

  const animateScroll = () => {
    currentTime += increment;
    const val = easeInOutQuart(currentTime / duration);
    const currentScroll = start + (change * val);
    if (direction === 'top') {
      element.scrollTo({top: currentScroll});
    } else {
      element.scrollTo({left: currentScroll});
    }

    if (currentTime < duration) {
      setTimeout(animateScroll, increment);
    } else {
      isScrolling = false;
      if (element == document.documentElement) checkBlockScroll();
      if (typeof returnFunction === 'function') returnFunction();
    }
  };

  animateScroll();
};
